/**
 * Assistant : bulle en bas à droite, sur toutes les pages, qui s'ouvre en
 * panneau de chat et se replie d'un clic. L'état ouvert/replié et la
 * conversation survivent à la navigation (état de session du navigateur).
 *
 * Les questions partent à `POST /api/chat` avec les derniers échanges ; la
 * réponse arrive au fil de l'eau (SSE) et s'affiche à mesure. Les requêtes SQL
 * que le modèle exécute sont montrées sous la réponse, repliées : on voit sur
 * quoi le chiffre repose. L'API ne garde rien : la mémoire, c'est ce panneau.
 */

import { Fragment, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { CHAT_MAX_TURNS, CHAT_MAX_TURN_CHARS, type ChatStreamEvent, type ChatTurn } from '@shared/types/chat';

import { useAuth } from '../hooks/useAuth';
import { ApiRequestError, streamChat } from '../lib/api';

interface ChatStep {
  sql: string;
  purpose: string;
  rows?: number;
  truncated?: boolean;
  error?: string;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  steps?: ChatStep[];
  error?: boolean;
}

const OPEN_KEY = 'pianoled.analytics.chat-open';
const HISTORY_KEY = 'pianoled.analytics.chat-history';
const GREETING: ChatMessage = {
  id: 0,
  role: 'assistant',
  text: 'Bonjour ! Pose-moi une question sur les données du dashboard : ventes, abonnements, pub, SAV… Je lis la base et je te réponds avec les chiffres.',
};

function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeSession(key: string, value: unknown) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // stockage indisponible : l'état ne survit pas à la navigation, sans plus
  }
}

/** Les échanges envoyés à l'API : texte seulement, les plus récents, sans la salutation ni les erreurs. */
function turnsFor(messages: ChatMessage[], question: string): ChatTurn[] {
  const turns: ChatTurn[] = messages
    .filter((message) => message.id !== GREETING.id && !message.error && message.text.trim())
    .map((message) => ({ role: message.role, content: message.text.slice(0, CHAT_MAX_TURN_CHARS) }));
  turns.push({ role: 'user', content: question });
  // Les rôles doivent alterner : deux messages consécutifs du même rôle (une
  // réponse coupée puis une nouvelle question) sont fusionnés.
  const merged: ChatTurn[] = [];
  for (const turn of turns) {
    const last = merged[merged.length - 1];
    if (last && last.role === turn.role) last.content = `${last.content}\n${turn.content}`.slice(0, CHAT_MAX_TURN_CHARS);
    else merged.push({ ...turn });
  }
  const recent = merged.slice(-CHAT_MAX_TURNS);
  // Le premier doit être une question.
  return recent[0]?.role === 'assistant' ? recent.slice(1) : recent;
}

/** Mise en forme légère : paragraphes, listes à tirets, **gras**, `code`. Pas de HTML : du texte découpé. */
function renderText(text: string): ReactNode {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={`l${blocks.length}`}>
        {list.map((item, index) => (
          <li key={index}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    list = [];
  };
  for (const paragraph of text.split(/\n{2,}/)) {
    for (const line of paragraph.split('\n')) {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
      if (bullet) {
        list.push(bullet[1] ?? '');
        continue;
      }
      flushList();
      if (line.trim()) blocks.push(<p key={`p${blocks.length}`}>{renderInline(line)}</p>);
    }
    flushList();
  }
  return blocks;
}

function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={index}>{part.slice(1, -1)}</code>;
    return <Fragment key={index}>{part}</Fragment>;
  });
}

export function ChatWidget() {
  const { token } = useAuth();
  const [open, setOpen] = useState(() => readSession(OPEN_KEY, false));
  const [messages, setMessages] = useState<ChatMessage[]>(() => readSession(HISTORY_KEY, [GREETING]));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => writeSession(OPEN_KEY, open), [open]);
  useEffect(() => writeSession(HISTORY_KEY, messages), [messages]);

  // Dernier message toujours visible ; le champ reprend le focus à l'ouverture.
  useEffect(() => {
    if (!open) return;
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [open, messages]);
  // Pas de focus automatique sur téléphone : le clavier s'ouvrirait et, sur
  // iOS, ferait défiler le panneau hors de l'écran (en-tête et bouton de
  // fermeture compris). L'utilisateur touche le champ quand il veut écrire.
  useEffect(() => {
    if (!open || busy) return;
    if (window.matchMedia('(max-width: 640px), (pointer: coarse)').matches) return;
    inputRef.current?.focus();
  }, [open, busy]);

  // Quitter la page en pleine réponse : on coupe le flux proprement.
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchLast = (patch: (message: ChatMessage) => ChatMessage) =>
    setMessages((current) => current.map((message, index) => (index === current.length - 1 ? patch(message) : message)));

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || busy || !token) return;
    setDraft('');
    const turns = turnsFor(messages, text);
    const answerId = Date.now() + 1;
    setMessages((current) => [
      ...current,
      { id: Date.now(), role: 'user', text },
      { id: answerId, role: 'assistant', text: '', steps: [] },
    ]);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat({ messages: turns }, token, (chunk: ChatStreamEvent) => {
        switch (chunk.type) {
          case 'text':
            patchLast((message) => ({ ...message, text: message.text + chunk.text }));
            break;
          case 'sql':
            patchLast((message) => ({ ...message, steps: [...(message.steps ?? []), { sql: chunk.sql, purpose: chunk.purpose }] }));
            break;
          case 'sql_result':
            patchLast((message) => {
              const steps = [...(message.steps ?? [])];
              const last = steps[steps.length - 1];
              if (last) {
                steps[steps.length - 1] = {
                  ...last,
                  rows: chunk.rows,
                  truncated: chunk.truncated,
                  ...(chunk.error ? { error: chunk.error } : {}),
                };
              }
              return { ...message, steps };
            });
            break;
          case 'error':
            patchLast((message) => ({ ...message, text: message.text ? `${message.text}\n\n${chunk.message}` : chunk.message, error: true }));
            break;
          case 'done':
            break;
        }
      }, controller.signal);
      patchLast((message) => (message.text.trim() ? message : { ...message, text: "Je n'ai pas de réponse à donner.", error: true }));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      const message =
        error instanceof ApiRequestError
          ? error.status === 503
            ? "L'assistant n'est pas encore branché sur ce serveur (clé API absente)."
            : error.message
          : "L'assistant est injoignable.";
      patchLast((current) => ({ ...current, text: message, error: true }));
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    setMessages([GREETING]);
  };

  if (!open) {
    return (
      <button type="button" className="chat-bubble" onClick={() => setOpen(true)} aria-label="Ouvrir l'assistant">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-4.2 3.4c-.4.3-.8 0-.8-.4V5.5Z" />
          <path d="M8 9h8M8 12h5" />
        </svg>
      </button>
    );
  }

  const lastIndex = messages.length - 1;

  return (
    <section className="chat" role="dialog" aria-label="Assistant">
      <header className="chat__head">
        <span className="app-header__dot" aria-hidden="true" />
        <span className="chat__title">Assistant</span>
        <span className="chat__spacer" />
        <button type="button" className="chat__icon" onClick={reset} aria-label="Nouvelle conversation" title="Nouvelle conversation">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 12a8 8 0 0 1 13.7-5.7L20 8.5M20 4v4.5h-4.5M20 12a8 8 0 0 1-13.7 5.7L4 15.5M4 20v-4.5h4.5" />
          </svg>
        </button>
        <button type="button" className="chat__icon" onClick={() => setOpen(false)} aria-label="Réduire l'assistant" title="Réduire">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M5 12h14" />
          </svg>
        </button>
      </header>

      <div className="chat__list" ref={listRef} aria-live="polite">
        {messages.map((message, index) => {
          const streaming = busy && index === lastIndex && message.role === 'assistant';
          const steps = message.steps ?? [];
          return (
            <Fragment key={message.id}>
              <div className={`chat__msg chat__msg--${message.role}${message.error ? ' chat__msg--error' : ''}`}>
                {message.role === 'user' ? message.text : renderText(message.text)}
                {streaming && (
                  <span className="chat__cursor" aria-hidden="true" />
                )}
                {streaming && !message.text && steps.length > 0 && <p>Je consulte la base…</p>}
              </div>
              {steps.length > 0 && (
                <details className="chat__steps">
                  <summary>
                    {steps.length} requête{steps.length > 1 ? 's' : ''} exécutée{steps.length > 1 ? 's' : ''}
                  </summary>
                  {steps.map((step, stepIndex) => (
                    <div key={stepIndex} className={`chat__step${step.error ? ' chat__step--error' : ''}`}>
                      {step.purpose || 'Requête'}
                      {step.error
                        ? ` — erreur : ${step.error}`
                        : step.rows !== undefined
                          ? ` — ${step.rows} ligne${step.rows > 1 ? 's' : ''}${step.truncated ? ' (tronqué)' : ''}`
                          : ' — en cours…'}
                      <code>{step.sql}</code>
                    </div>
                  ))}
                </details>
              )}
            </Fragment>
          );
        })}
      </div>

      <form className="chat__form" onSubmit={(event) => void send(event)}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={token ? 'Une question sur les données…' : 'Connecte-toi pour utiliser l\'assistant'}
          aria-label="Votre question"
          autoComplete="off"
          maxLength={CHAT_MAX_TURN_CHARS}
          disabled={!token}
        />
        <button type="submit" className="btn btn--primary" disabled={!draft.trim() || busy || !token} aria-label="Envoyer">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 12h15M13 6l6 6-6 6" />
          </svg>
        </button>
      </form>
    </section>
  );
}
